import { create } from 'zustand';
import { format, startOfMonth, endOfMonth } from 'date-fns';

import { PlannerTaskTab, TaskStatusFilter } from './types';

interface PlannerMessagesStore {
  tab: PlannerTaskTab;
  status: TaskStatusFilter;
  startDate: string;
  endDate: string;
  costCenterIds: string[];
  selectedTaskIds: string[];
  pageIndex: number;
  // The window a home tile counted, handed over when the user clicks it.
  pendingRange: { startDate: string; endDate: string } | null;
  // Which plan unit selection the prefill already applied (comma-joined cost
  // center ids), and whether the planner has since taken the filter over.
  prefilledFromPlanUnits: string | null;
  costCentersOwnedByUser: boolean;

  setTab: (tab: PlannerTaskTab) => void;
  setStatus: (status: TaskStatusFilter) => void;
  setDateRange: (startDate: string, endDate: string) => void;
  setCostCenterIds: (ids: string[]) => void;
  setSelectedTaskIds: (ids: string[]) => void;
  toggleTaskSelection: (taskId: string) => void;
  clearSelection: () => void;
  setPageIndex: (index: number) => void;
  openFromTile: (
    startDate: string,
    endDate: string,
    costCenterIds: string[]
  ) => void;
  clearPendingRange: () => void;
  markCostCentersPrefilled: (signature: string) => void;
  markCostCentersOwnedByUser: () => void;
  resetFilters: () => void;
}

const DATE_FORMAT = 'yyyy-MM-dd';

const getDefaultStartDate = () => format(startOfMonth(new Date()), DATE_FORMAT);

const getDefaultEndDate = () => format(endOfMonth(new Date()), DATE_FORMAT);

export const usePlannerMessagesStore = create<PlannerMessagesStore>((set) => ({
  tab: PlannerTaskTab.ALL,
  status: TaskStatusFilter.OPEN,
  startDate: getDefaultStartDate(),
  endDate: getDefaultEndDate(),
  costCenterIds: [],
  selectedTaskIds: [],
  pageIndex: 0,
  pendingRange: null,
  prefilledFromPlanUnits: null,
  costCentersOwnedByUser: false,

  setTab: (tab) => set({ tab, selectedTaskIds: [], pageIndex: 0 }),
  setStatus: (status) => set({ status, selectedTaskIds: [], pageIndex: 0 }),
  setDateRange: (startDate, endDate) =>
    set({ startDate, endDate, selectedTaskIds: [], pageIndex: 0 }),
  setCostCenterIds: (costCenterIds) =>
    set({ costCenterIds, selectedTaskIds: [], pageIndex: 0 }),
  setSelectedTaskIds: (selectedTaskIds) => set({ selectedTaskIds }),
  toggleTaskSelection: (taskId) =>
    set((state) => ({
      selectedTaskIds: state.selectedTaskIds.includes(taskId)
        ? state.selectedTaskIds.filter((id) => id !== taskId)
        : [...state.selectedTaskIds, taskId]
    })),
  clearSelection: () => set({ selectedTaskIds: [] }),
  setPageIndex: (pageIndex) => set({ pageIndex, selectedTaskIds: [] }),
  openFromTile: (startDate, endDate, costCenterIds) =>
    set({
      // Write the window straight away: leaving it to the effect makes the
      // first render query the previous window, so every tile click costs a
      // wasted round trip per query and briefly shows the wrong rows.
      startDate,
      endDate,
      pendingRange: { startDate, endDate },
      // Tiles count open tasks only, and this store survives the tab unmount:
      // a HISTORY filter from an earlier visit would reintroduce the very count
      // mismatch the hand-off is meant to remove.
      status: TaskStatusFilter.OPEN,
      // The tile counts over the home page's plan unit selection, so the list
      // has to scope to the same cost centers - otherwise the number on the
      // tile and the rows underneath answer different questions. A tile click
      // is an explicit request for that tile's scope, so it overrules a filter
      // the planner set earlier (#1939) and hands the prefill its own
      // signature, which keeps a *later* home page change propagating.
      costCenterIds,
      costCentersOwnedByUser: false,
      prefilledFromPlanUnits: costCenterIds.join(','),
      selectedTaskIds: [],
      pageIndex: 0
    }),
  clearPendingRange: () => set({ pendingRange: null }),
  markCostCentersPrefilled: (prefilledFromPlanUnits) =>
    set({ prefilledFromPlanUnits }),
  markCostCentersOwnedByUser: () => set({ costCentersOwnedByUser: true }),
  resetFilters: () =>
    set({
      tab: PlannerTaskTab.ALL,
      status: TaskStatusFilter.OPEN,
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate(),
      costCenterIds: [],
      selectedTaskIds: [],
      pageIndex: 0,
      pendingRange: null,
      prefilledFromPlanUnits: null,
      costCentersOwnedByUser: false
    })
}));
